import { useRef } from 'react'
import { format, isAfter, parse, startOfDay } from 'date-fns'
import clsx from 'clsx'
import { KeyboardEvent } from 'react'

import { Button } from '@/ui/Button'
import { Checkbox } from '@/ui/Checkbox'
import { DatePicker } from '@/ui/DatePicker'
import { SuggestionInputTable } from '@/ui/SuggestionInputTable'
import { Popup, PopupRef } from '@/ui/Popup'
import { getFormattedDate } from '@/helpers/time'

import styles from './DateSelect.module.scss'

import { UseFormReturn } from 'react-hook-form'
import { DisruptionFormValues } from '@/pages/Disruptions/types'
import { formatInTimeZone } from 'date-fns-tz'
import { DisruptionStatus } from '@/types/disruptions'
import { isValidTimeFormat } from '@/helpers/isValidTimeFormat'

type Props = {
    form: UseFormReturn<DisruptionFormValues>
    label: 'Start' | 'End'
    from?: Date
    to?: Date
    fromTimeAllDay: boolean
    fromTime: string
    toTimeAllDay: boolean
    toTime: string
    setFrom: (date?: Date) => void
    setTo: (date?: Date) => void
    setFromTime: (time: string) => void
    setToTime: (time: string) => void
    setFromTimeAllDay: (value: boolean) => void
    setToTimeAllDay: (value: boolean) => void
}

const TODAY = startOfDay(new Date())

const allTimeOptions = () => {
    const times = []

    for (let hour = 5; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            // Format hour and minute with leading zeros
            const formattedHour = hour.toString().padStart(2, '0')
            const formattedMinute = minute.toString().padStart(2, '0')

            times.push({
                id: crypto.randomUUID(),
                label: `${formattedHour}:${formattedMinute}`,
                value: `${formattedHour}:${formattedMinute}`,
            })
        }
    }

    return times
}

export const DateSelect = ({
    form,
    label,
    from,
    to,
    fromTimeAllDay,
    fromTime,
    toTimeAllDay,
    toTime,
    setFrom,
    setTo,
    setFromTime,
    setToTime,
    setFromTimeAllDay,
    setToTimeAllDay,
}: Props) => {
    const start = form.watch('start')
    const end = form.watch('end')
    const popupRef = useRef<PopupRef>(null)

    const fromTimeOptions = allTimeOptions()
    const toTimeOptions = allTimeOptions()
    const toIsProvided = to || (toTime || toTimeAllDay) // TO is considered provided if date selected OR time entered
    const isButtonDisabled =
        (!fromTimeAllDay && !fromTime) || // Ensure FROM time is populated
        (!isValidTimeFormat(fromTime) && !fromTimeAllDay) ||
        (toIsProvided && !toTimeAllDay && !toTime) || // If TO is provided in any way, ensure time is populated
        (toIsProvided && !toTimeAllDay && !isValidTimeFormat(toTime)) || // Validate TO time format if provided
        (toIsProvided && !!fromTime && !!toTime && !fromTimeAllDay && !toTimeAllDay
            ? isAfter(parse(fromTime, 'HH:mm', from || TODAY), parse(toTime, 'HH:mm', to || TODAY))
            : false) // If TO time provided, ensure TO time is after FROM time

    const applyFilters = () => {
        if (isButtonDisabled) {
            return
        }

        const fromTimeSelected = fromTimeAllDay ? '00:00' : fromTime
        const toTimeSelected = toTimeAllDay ? '23:59' : toTime
        const fromDateToUse = from || TODAY
        const toDateToUse = to || TODAY // Default TO to TODAY if only time is provided

        const fromFilter = getFormattedDate({ time: fromTimeSelected, date: format(fromDateToUse, 'dd/MM/yyyy') })
        const toFilter = (toTime || toTimeAllDay) ? getFormattedDate({ time: toTimeSelected, date: format(toDateToUse, 'dd/MM/yyyy') }) : undefined

        form.setValue('start', fromFilter, { shouldDirty: true })
        form.setValue('end', toFilter, { shouldDirty: true })

        // Update status based on date range
        const now = new Date()
        const oneHourFromNow = new Date()
        oneHourFromNow.setHours(oneHourFromNow.getHours() + 1)
        const startDate = new Date(fromFilter)
        const endDate = toFilter ? new Date(toFilter) : undefined

        if (startDate > oneHourFromNow) {
            form.setValue('status', DisruptionStatus.Upcoming)
        } else if (startDate < now && endDate && endDate < now) {
            form.setValue('status', DisruptionStatus.Cleared)
        } else {
            form.setValue('status', DisruptionStatus.Live)
        }

        popupRef.current?.closePopup()
    }

    const clearFrom = () => {
        setFrom(undefined)
        setFromTime('')
        setFromTimeAllDay(false)
    }

    const clearTo = () => {
        setTo(undefined)
        setToTime('')
        setToTimeAllDay(false)
    }

    const cancel = () => {
        popupRef.current?.closePopup()
        clearFrom()
        clearTo()
        form.setValue('start', '')
        form.setValue('end', '')
    }

    const getButtonLabel = (label: 'Start' | 'End') => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

        if (!start) {
            return label
        }

        const isStartTimeSpecified = format(start, 'HH:mm') !== '00:00' && format(start, 'HH:mm') !== '23:59'
        const isEndTimeSpecified = end ? format(end, 'HH:mm') !== '00:00' && format(end, 'HH:mm') !== '23:59' : false

        const formatStart = formatInTimeZone(start, timezone, isStartTimeSpecified ? 'dd/MM/yy, HH:mm' : 'dd/MM/yy')
        const formatEnd = end
            ? formatInTimeZone(end, timezone, isEndTimeSpecified ? 'dd/MM/yy, HH:mm' : 'dd/MM/yy')
            : '-'

        return label === 'Start' ? formatStart : formatEnd
    }

    const handleTimeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', ':']
        // Allow numbers 0-9
        if (/^\d$/.test(e.key)) {
            return
        }
        if (allowedKeys.includes(e.key)) {
            return
        }
        e.preventDefault()
    }
    const handleFromTimeChange = (value: string) => {
        if (value.length === 2 && !value.includes(':')) {
            setFromTime(`${value}:`)
        } else {
            setFromTime(value)
        }
    }
    const handleToTimeChange = (value: string) => {
        if (value.length === 2 && !value.includes(':')) {
            setToTime(`${value}:`)
        } else {
            setToTime(value)
        }
    }

    return (
        <Popup
            ref={popupRef}
            triggerComponent={() => (
                <Button className={styles.trigger} size="small" variant="transparent">
                    {getButtonLabel(label)}
                </Button>
            )}
            attachToTrigger
            placement="bottom-end"
            className={styles.popup}
        >
            <div className={styles.dateContainer}>
                <div className={styles.dateWrapper}>
                    <div className={styles.titleWrapper}>
                        <h4 className={styles.title}>From</h4>
                        <button
                            className={clsx(styles.clear, {
                                [styles.clearActive]: from && (fromTime || fromTimeAllDay),
                            })}
                            onClick={clearFrom}
                        >
                            Clear
                        </button>
                    </div>
                    <DatePicker
                        className={styles.datePicker}
                        value={from ?? null}
                        onChange={setFrom}
                        disableFutureDates={false}
                        highlightTodayIfSelected
                    />
                    <div className={styles.timeContainer}>
                        <SuggestionInputTable
                            placeholder={fromTimeAllDay ? 'All day' : 'Time'}
                            options={fromTimeOptions}
                            value={fromTime}
                            className={styles.timePopup}
                            pattern="[0-9]{2}:[0-9]{2}"
                            maxLength={5}
                            disabled={fromTimeAllDay}
                            onKeyDown={handleTimeKeyDown}
                            onChange={handleFromTimeChange}
                        />
                        <div className={styles.allDayWrapper}>
                            <p className={styles.label}>All day</p>
                            <Checkbox
                                label="All day"
                                variant="withoutLabel"
                                checked={fromTimeAllDay}
                                onChange={(value) => {
                                    value && setFromTime('')
                                    setFromTimeAllDay(value)
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.dateWrapper}>
                    <div className={styles.titleWrapper}>
                        <h4 className={styles.title}>To</h4>
                        <button
                            className={clsx(styles.clear, {
                                [styles.clearActive]: to,
                            })}
                            onClick={clearTo}
                        >
                            Clear
                        </button>
                    </div>
                    <DatePicker
                        className={styles.datePicker}
                        value={to ?? null}
                        onChange={setTo}
                        disableFutureDates={false}
                        highlightTodayIfSelected
                    />
                    <div className={styles.timeContainer}>
                        <SuggestionInputTable
                            placeholder={toTimeAllDay ? 'All day' : 'Time'}
                            options={toTimeOptions}
                            value={toTime}
                            onChange={handleToTimeChange}
                            disabled={toTimeAllDay}
                            pattern="[0-9]{2}:[0-9]{2}"
                            maxLength={5}
                            onKeyDown={handleTimeKeyDown}
                            className={styles.timePopup}
                        />
                        <div className={styles.allDayWrapper}>
                            <p className={styles.label}>All day</p>
                            <Checkbox
                                label="All day"
                                variant="withoutLabel"
                                checked={toTimeAllDay}
                                onChange={(value) => {
                                    value && setToTime('')
                                    setToTimeAllDay(value)
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <Button rounded size="small" variant="tertiary" onClick={cancel}>
                    Cancel
                </Button>
                <Button rounded size="small" variant="secondary" disabled={isButtonDisabled} onClick={applyFilters}>
                    Done
                </Button>
            </div>
        </Popup>
    )
}
